/**
 * <RecoverySlotStep> — l'étape « Quel nouveau créneau ? » (organism).
 *
 * Les créneaux proposés pour rattraper la séance déplacée, plus la sortie « Je choisirai plus
 * tard » : le parent peut noter l'absence sans s'engager tout de suite, le rattrapage revient
 * alors dans « À faire ». Jamais d'impasse — c'est la règle du parcours.
 *
 * « Meilleur groupe » est une chip, pas un tri : le serveur donne l'ordre, on ne le refait pas.
 *
 * Pendant que les créneaux arrivent, l'étape en montre la forme : trois cartes d'option au visage
 * du tuteur, et la place de « Je choisirai plus tard ».
 *
 * `voice` : l'élève lit les mots de son formulaire d'absence (`RecoverLiveSessionComponent`) —
 * « Rattraper plus tard », et le motif « aucune séance disponible », tutoyé.
 */
import React from "react"

import { Avatar } from "../../atoms/Avatar"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Stack } from "../../atoms/containers/Stack"
import { DelayedSkeleton, Skeleton } from "../../atoms/Skeleton"
import { Text } from "../../atoms/typography/Text"
import { OptionCard } from "../../molecules/OptionCard"
import { OptionCardSkeleton } from "../../molecules/OptionCard/OptionCardSkeleton"
import { type UIParentRecoverySlot } from "../../../ports/primary/ui/types"
import { formatDayTime, formatDuration } from "../../../ports/primary/ui/formatters/dateFormatter"
import { tutorShortName } from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import { type Voice } from "../../utils/voice"

const TEXTS: Record<Voice, { emptyMessage: string; chooseLater: string }> = {
    parent: {
        emptyMessage:
            "Aucun créneau de rattrapage n'est disponible pour l'instant. Notez l'absence, nous vous proposerons un créneau.",
        chooseLater: "Je choisirai plus tard",
    },
    student: {
        emptyMessage:
            "Aucune séance de rattrapage disponible pour le moment. Tu recevras un email lorsqu'il y aura des séances disponibles.",
        chooseLater: "Rattraper plus tard",
    },
}

/** Le visage du tuteur d'un créneau. */
const TUTOR_AVATAR_SIZE = 44

export type RecoverySlotStepProps = {
    slots: UIParentRecoverySlot[]
    selectedId?: UIParentRecoverySlot["id"]
    onSelect: (slot: UIParentRecoverySlot) => void
    /** « Je choisirai plus tard » — absence notée, rattrapage remis dans « À faire ». */
    onChooseLater?: () => void
    /**
     * Ce que dit l'étape quand aucun créneau n'est proposé. Le défaut promet un créneau plus
     * tard : un parcours qui sait que le rattrapage est refusé pour de bon doit le remplacer,
     * sinon l'écran promet ce qui ne viendra pas.
     */
    emptyMessage?: string
    loading?: boolean
    /** À qui l'étape parle — `"parent"` par défaut. */
    voice?: Voice
    testID?: string
}

export function RecoverySlotStep(props: RecoverySlotStepProps) {
    const { slots, selectedId, onSelect, onChooseLater, loading = false, voice = "parent", testID } = props
    const texts = TEXTS[voice]
    const emptyMessage = props.emptyMessage ?? texts.emptyMessage

    if (loading) {
        return (
            <DelayedSkeleton testID={testID} accessibilityLabel="Chargement des créneaux">
                <Stack gap="4">
                    <Stack gap="2">
                        <OptionCardSkeleton leadingSize={TUTOR_AVATAR_SIZE} labelWidth={170} descriptionWidth={110} />
                        <OptionCardSkeleton leadingSize={TUTOR_AVATAR_SIZE} labelWidth={150} descriptionWidth={120} />
                        <OptionCardSkeleton leadingSize={TUTOR_AVATAR_SIZE} labelWidth={160} descriptionWidth={100} />
                    </Stack>
                    {onChooseLater != null && (
                        <Stack align="center">
                            <Skeleton textSize="text-base" width={170} />
                        </Stack>
                    )}
                </Stack>
            </DelayedSkeleton>
        )
    }

    return (
        <Stack gap="4" testID={testID}>
            {slots.length === 0 ? (
                <Text color="text-muted">{emptyMessage}</Text>
            ) : (
                <Stack gap="2">
                    {slots.map((slot) => (
                        <OptionCard
                            key={slot.id}
                            label={formatDayTime(slot.date)}
                            description={[
                                slot.tutor != null ? `avec ${slot.tutor.firstname}` : null,
                                formatDuration(slot.duration),
                            ]
                                .filter(Boolean)
                                .join(" · ")}
                            badge={slot.recommended === true ? "Meilleur groupe" : undefined}
                            badgeVariant="success"
                            selected={slot.id === selectedId}
                            onPress={() => onSelect(slot)}
                            leading={
                                slot.tutor != null ? (
                                    <Avatar
                                        uri={slot.tutor.avatarUrl}
                                        name={tutorShortName(slot.tutor)}
                                        size={TUTOR_AVATAR_SIZE}
                                        signed
                                    />
                                ) : undefined
                            }
                        />
                    ))}
                </Stack>
            )}

            {onChooseLater != null && <TextButton label={texts.chooseLater} onPress={onChooseLater} />}
        </Stack>
    )
}
